import { cacheService } from './cacheService';
import { supabase } from '@/integrations/supabase/client';

export class GoogleAdsToggleService {
  private static readonly CACHE_KEY = 'display_google_ads';
  private static readonly CACHE_VERSION = '1.0.0';
  private static readonly DB_SETTING_KEY = 'display_google_ads';
  private static readonly CACHE_TTL_SETTING_KEY = 'cache_short_timer_cache_timer_short_ttl_minutes';
  private static readonly TTL_CACHE_KEY = 'cache_ttl_setting';
  private static readonly TTL_CACHE_VERSION = '1.0.0';
  private static readonly DEFAULT_VALUE = false; // Safe default - no ads
  private static readonly DEFAULT_CACHE_TTL_MINUTES = 30; // Fallback if setting not found (alternative: 5 minutes)
  private static readonly TTL_SETTING_CACHE_DURATION_MS = 60 * 60 * 1000; // Cache TTL setting for 1 hour

  /**
   * Get cache TTL from settings table (with caching) or use default
   */
  private static async getCacheTTLMinutes(): Promise<number> {
    return await cacheService.get<number>(
      this.TTL_CACHE_KEY,
      async () => {
        try {
          const { data, error } = await supabase
            .from('settings')
            .select('value')
            .eq('key', this.CACHE_TTL_SETTING_KEY)
            .single();
            
          if (error || !data?.value) {
            console.warn(`Failed to fetch ${this.CACHE_TTL_SETTING_KEY} setting, using default ${this.DEFAULT_CACHE_TTL_MINUTES} minutes`);
            return this.DEFAULT_CACHE_TTL_MINUTES;
          }
          
          const ttlMinutes = parseInt(data.value, 10);
          
          // Validate range (1-1440 minutes = 1 minute to 24 hours)
          if (isNaN(ttlMinutes) || ttlMinutes < 1 || ttlMinutes > 1440) {
            console.warn(`Invalid cache TTL setting: ${data.value}, using default ${this.DEFAULT_CACHE_TTL_MINUTES} minutes`);
            return this.DEFAULT_CACHE_TTL_MINUTES;
          }
          
          return ttlMinutes;
        } catch (error) {
          console.error('Error fetching cache TTL setting:', error);
          return this.DEFAULT_CACHE_TTL_MINUTES;
        }
      },
      {
        ttlMs: this.TTL_SETTING_CACHE_DURATION_MS,
        version: this.TTL_CACHE_VERSION
      }
    );
  }

  /**
   * Check if Google Ads should be displayed (with caching)
   */
  static async shouldDisplayGoogleAds(): Promise<boolean> {
    const cacheTTLMinutes = await this.getCacheTTLMinutes();
    const cacheTTLMs = cacheTTLMinutes * 60 * 1000;

    return await cacheService.get<boolean>(
      this.CACHE_KEY,
      async () => {
        try {
          const { data, error } = await supabase
            .from('settings')
            .select('value')
            .eq('key', this.DB_SETTING_KEY)
            .single();
            
          if (error || !data) {
            console.warn(`Failed to fetch ${this.DB_SETTING_KEY} setting:`, error);
            return this.DEFAULT_VALUE;
          }
          
          return data.value === 'true';
        } catch (error) {
          console.error(`Error fetching ${this.DB_SETTING_KEY}:`, error);
          return this.DEFAULT_VALUE;
        }
      },
      {
        ttlMs: cacheTTLMs,
        version: this.CACHE_VERSION
      }
    );
  }

  /**
   * Get cached Google Ads setting synchronously
   */
  static shouldDisplayGoogleAdsSync(): boolean {
    // For sync method, use default TTL since we can't await the setting fetch
    const defaultTTLMs = this.DEFAULT_CACHE_TTL_MINUTES * 60 * 1000;
    
    const cachedValue = cacheService.getSync<boolean>(this.CACHE_KEY, {
      version: this.CACHE_VERSION,
      ttlMs: defaultTTLMs
    });
    return cachedValue !== null ? cachedValue : this.DEFAULT_VALUE;
  }

  /**
   * Initialize cache on app startup
   */
  static async initializeCache(): Promise<void> {
    try {
      await this.shouldDisplayGoogleAds();
      console.log('Google Ads toggle cache initialized successfully');
    } catch (error) {
      console.warn('Failed to initialize Google Ads toggle cache:', error);
    }
  }
} 